import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import { Icon } from "@mediamath/design-system-eng";
import SmartLink from "core/shared/util/SmartLink";
import { Colors, BrandingColors } from "design-system";
import {
  StAnchor,
  StUserSpan,
  interactiveBlockStyles,
} from "core/app/presenter/sideNav/SideNavStyles";

// TODO: remove css() dependency when migrating off Emotion to CSS modules
const sharedLinkStyles = (indent) => css`
  flex: 1;
  align-self: stretch;
  display: flex;
  align-items: center;
  padding: ${indent ? "6px 0" : "18px 0"};
  ${interactiveBlockStyles};
`;

const StAnchorLink = styled(StAnchor, {
  shouldForwardProp: (prop) => prop !== "indent",
})`
  ${({ indent }) => sharedLinkStyles(indent)}
`;

const StRouterLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== "indent",
})`
  ${({ indent }) => sharedLinkStyles(indent)}
`;

const StSmartLink = styled(SmartLink, {
  shouldForwardProp: (prop) => prop !== "indent",
})`
  ${({ indent }) => sharedLinkStyles(indent)}
`;

const NavItemLink = ({
  isExternal,
  isSmartLink,
  to,
  indent,
  children,
  ...props
}) => {
  if (isExternal) {
    return (
      <StAnchorLink
        href={to}
        indent={indent}
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {children}
      </StAnchorLink>
    );
  }
  const LinkComp = isSmartLink ? StSmartLink : StRouterLink;
  return (
    <LinkComp to={to} indent={indent} {...props}>
      {children}
    </LinkComp>
  );
};

NavItemLink.propTypes = {
  children: PropTypes.node.isRequired,
  indent: PropTypes.bool,
  isExternal: PropTypes.bool,
  isSmartLink: PropTypes.bool,
  to: PropTypes.string,
};

const getHoverBackground = ({ noHover }) =>
  noHover ? "none" : BrandingColors.Hover;

export const StMenuItem = styled("div", {
  shouldForwardProp: (prop) =>
    !["active", "sub", "noHover", "block"].includes(prop),
})`
  width: 100%;
  display: ${(props) => (props.block ? "block" : "flex")};
  align-items: center;
  justify-content: space-between;
  padding: ${(props) => (props.sub ? "3px 18px" : "0 18px")};
  padding-left: ${(props) => (props.sub ? "37px" : "18px")};
  margin-left: 0;
  background: ${(props) =>
    props.active && !props.sub ? BrandingColors.ActiveItem : "none"};
  border-left: ${(props) => {
    if (props.sub) return "7px solid transparent";
    return props.active
      ? `7px solid ${BrandingColors.Accent}`
      : "7px solid transparent";
  }};
  position: relative;
  transition: background 150ms ease, border-color 150ms ease;

  & > * {
    color: ${Colors.White} !important;
    opacity: ${(props) => (props.active ? "1.0" : "0.75")} !important;
    position: relative;
    z-index: 1;
  }

  &::before {
    content: ${(props) => (props.sub ? '"•"' : '""')};
    color: ${BrandingColors.Accent};
    font-size: 16px;
    line-height: 1;
    position: absolute;
    left: 19px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    opacity: ${(props) => (props.active ? 1 : 0)};
    transition: opacity 150ms ease;
  }

  &:hover::before {
    opacity: 1;
  }

  &:hover {
    background: ${getHoverBackground};
    border-left-color: ${(props) =>
      props.noHover || props.sub ? "transparent" : BrandingColors.Accent};
    & > * {
      opacity: 1 !important;
    }
  }
`;

export const StMenuText = styled("span")`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StMenuIcon = styled(Icon, {
  shouldForwardProp: (prop) => prop !== "expanded",
})`
  transform: ${(props) =>
    props.expanded ? "rotate(-90deg)" : "rotate(90deg)"};
  transition: transform 300ms ease-in-out;
  font-size: 9px !important;
  line-height: 9px;
  width: 9px !important;
  height: 9px !important;
  margin: 0 !important;
  display: block;
  cursor: pointer;
  z-index: 1;

  &:after {
    content: "";
    position: absolute;
    inset: -8px;
  }
`;

const MenuItem = React.memo(
  ({
    active,
    beta = false,
    betaLabel,
    data: { key, link: path, subMenu },
    expanded,
    isExternal = false,
    isSmartLink = true,
    label,
    noHover = false,
    onSelect = () => {},
    preventNav = false,
    sub,
    toggleClick,
    user,
  }) => {
    const handleIconClick = useCallback(() => toggleClick(key), [
      key,
      toggleClick,
    ]);

    const handleMenuClick = useCallback(
      (evt) => {
        onSelect(key);
        if (subMenu) {
          if (preventNav) evt.preventDefault();
          toggleClick(key);
        }
      },
      [key, subMenu, preventNav, onSelect, toggleClick]
    );

    return (
      <StMenuItem active={active} noHover={noHover} sub={sub}>
        <NavItemLink
          isExternal={isExternal}
          isSmartLink={isSmartLink}
          to={path}
          indent={sub}
          onClick={handleMenuClick}
        >
          <StMenuText>
            <span>
              {label}
              {beta && (
                <sup>
                  &nbsp;
                  {betaLabel}
                </sup>
              )}
            </span>
            {user && <StUserSpan>{user}</StUserSpan>}
          </StMenuText>
        </NavItemLink>
        {subMenu && (
          <StMenuIcon
            expanded={expanded}
            name="arrow-fill"
            onClick={handleIconClick}
          />
        )}
      </StMenuItem>
    );
  }
);

MenuItem.displayName = "MenuItem";
MenuItem.propTypes = {
  active: PropTypes.bool,
  beta: PropTypes.bool,
  betaLabel: PropTypes.string,
  data: PropTypes.shape({
    flag: PropTypes.string,
    key: PropTypes.string,
    link: PropTypes.string,
    subMenu: PropTypes.arrayOf(PropTypes.shape({})),
    to: PropTypes.string,
  }),
  expanded: PropTypes.bool,
  isExternal: PropTypes.bool,
  isSmartLink: PropTypes.bool,
  label: PropTypes.string,
  noHover: PropTypes.bool,
  onSelect: PropTypes.func,
  preventNav: PropTypes.bool,
  sub: PropTypes.bool,
  toggleClick: PropTypes.func,
  user: PropTypes.string,
};

export default MenuItem;
